import React from "react";
import { Link } from "react-router-dom";
import { Avatar, Username, VipBadge } from "./UserBadge";
import { CARD_BACKGROUNDS } from "../constants/loadout";
import { User as UserIcon, Crown, Mail } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function ProfileSidebar({ user }) {
  if (!user) return null;

  const accentColor = user.group_color || "#FF4F00";
  const publicUrl = process.env.PUBLIC_URL || "/forum";
  
  const cardBgOpt = CARD_BACKGROUNDS.find((b) => b.id === user.loadout?.card_background);
  const bannerImage = (user.cover_banner_url || user.banner_url)
    ? (user.cover_banner_url || user.banner_url)
    : (cardBgOpt && cardBgOpt.id !== "none" && cardBgOpt.style?.backgroundImage)
      ? cardBgOpt.style.backgroundImage
      : `${publicUrl}/banners/default_profile_banner.png`;

  return (
    <div className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl rounded-sm profile-sidebar" data-testid="profile-sidebar">
      {/* 1. Header with ID */}
      <div className="px-4 py-2.5 border-b border-[#141824] bg-[#06080d] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserIcon className="w-4 h-4 text-[var(--tf-primary)]" />
          <h4 className="font-chakra font-black text-xs uppercase tracking-widest text-white">
            PROFIL UTILIZATOR
          </h4>
        </div>
        <span className="text-[10px] text-zinc-400 font-mono">ID: #{user.id?.slice ? user.id.slice(0, 6) : user.id || "001"}</span>
      </div>

      <div>
        {/* 2. Banner with Fallback Mesh */}
        <div 
          className="h-32 w-full relative bg-[#121824] bg-cover bg-center overflow-hidden"
          style={{
            backgroundImage: `url(${bannerImage}), linear-gradient(135deg, #161e2e 0%, #0a0d14 100%)`
          }}
        >
          {/* Subtle Cyberpunk Grid Overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff0a_1px,transparent_1px)] [background-size:12px_12px] opacity-60" />
          {/* Bottom Vignette Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#090c12] via-[#090c12]/40 to-transparent" />
        </div>

        {/* 3. Centered Elevated Avatar (Unclipped) */}
        <div className="relative -mt-14 flex justify-center z-20">
          <div 
            className="relative p-1 rounded-full bg-[#090c12] border-2 shadow-[0_0_25px_rgba(0,0,0,0.95)] hover:scale-105 transition-transform"
            style={{ borderColor: accentColor }}
          >
            <Avatar user={user} size={84} />
            <span className="absolute bottom-1 right-1 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-[#090c12] shadow-[0_0_8px_#10b981]" title="Online" />
          </div>
        </div>

        {/* 4. Username & Badges Row */}
        <div className="px-4 pt-3 text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Username user={user} className="text-xl font-bold font-chakra tracking-wide text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]" asText />
          </div>
          
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {((user.loadout?.banner_badge && user.loadout.banner_badge !== "none") || (user.vip_tier && user.vip_tier !== "none") || user.isVip || user.is_vip) && (
              <VipBadge tier={user.loadout?.banner_badge || user.vip_tier || "gold"} />
            )}
            
            {(user.role === "root" || user.isRoot || user.is_root) && (
              <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-500 text-black border border-amber-400 font-chakra notranslate shadow-sm" translate="no">
                ROOT
              </span>
            )}
            {user.role === "admin" && (
              <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white border border-red-500 font-chakra notranslate shadow-sm" translate="no">
                ADMIN
              </span>
            )}
            {user.role === "mod" && (
              <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-blue-600 text-white border border-blue-500 font-chakra notranslate shadow-sm" translate="no">
                MODERATOR
              </span>
            )}
            {user.group_name && (
              <span 
                className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-black/60 text-zinc-200 border border-[#181e2b] font-chakra notranslate"
                style={{ color: accentColor }}
                translate="no"
              >
                {user.group_name}
              </span>
            )}

            <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-emerald-950/60 text-emerald-400 border border-emerald-500/30 flex items-center gap-1 font-mono notranslate shadow-sm" translate="no">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              ONLINE
            </span>
          </div>

          <p className="text-xs text-zinc-300 italic max-w-xs mx-auto leading-relaxed font-sans px-2 pt-0.5">
            "{user.personal_quote || user.signature || "EST. 2010"}"
          </p>
        </div>

        <div className="p-4 pt-3 space-y-3.5">
          {/* 5. Stats 3-Column Grid */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-[#06080d] border border-[#141824] p-2.5 text-center rounded-sm">
              <span className="text-xs text-amber-400 font-bold block">★</span>
              <span className="text-sm font-extrabold text-amber-400 font-chakra block">{user.points || 0}</span>
              <span className="text-[9px] text-zinc-400 uppercase tracking-wider font-chakra font-bold">Puncte</span>
            </div>
            <div className="bg-[#06080d] border border-[#141824] p-2.5 text-center rounded-sm">
              <span className="text-xs text-cyan-400 font-bold block">💬</span>
              <span className="text-sm font-extrabold text-white font-chakra block">{user.message_count || user.messages || 0}</span>
              <span className="text-[9px] text-zinc-400 uppercase tracking-wider font-chakra font-bold">Mesaje</span>
            </div>
            <div className="bg-[#06080d] border border-[#141824] p-2.5 text-center rounded-sm">
              <span className="text-xs text-orange-400 font-bold block">⚡</span>
              <span className="text-sm font-extrabold text-white font-chakra block">{user.reaction_score || user.reactions || user.reputation || 0}</span>
              <span className="text-[9px] text-zinc-400 uppercase tracking-wider font-chakra font-bold">Reacții</span>
            </div>
          </div>

          {/* Daily Bonus Claim Button */}
          <div>
            <button
              onClick={async () => {
                try {
                  const res = await api.post("/points/daily-checkin");
                  toast.success(res.data?.message || "Ai primit +15 IDG Points!");
                  window.location.reload();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Ai revendicat deja bonusul zilnic!");
                }
              }}
              className="w-full py-2 bg-gradient-to-r from-amber-500/15 via-amber-500/25 to-amber-500/15 hover:from-amber-500/30 hover:to-amber-500/30 border border-amber-500/40 hover:border-amber-400 text-amber-300 font-chakra font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-md cursor-pointer rounded-sm"
            >
              <span>🎁</span>
              <span>REVENDICĂ +15 IDG ZILNIC</span>
            </button>
          </div>

          {/* 6. Custom Details Grid */}
          <div className="space-y-1.5 bg-[#06080d] p-3 border border-[#141824] text-xs font-mono rounded-sm">
            <div className="flex items-center justify-between py-1 border-b border-[#121620]">
              <span className="text-zinc-400 flex items-center gap-1.5">
                <span>📍</span> Locație
              </span>
              <span className="font-bold text-white tracking-wide">{user.location || "INDUNGI"}</span>
            </div>
            
            <div className="flex items-center justify-between py-1 border-b border-[#121620]">
              <span className="text-zinc-400 flex items-center gap-1.5">
                <span>🎮</span> Steam
              </span>
              {user.steam_id || user.steamProfile ? (
                <a 
                  href={user.steamProfile || `https://steamcommunity.com/profiles/${user.steam_id}`} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="font-bold text-cyan-400 hover:underline"
                >
                  Profil Steam
                </a>
              ) : (
                <span className="text-zinc-500">Neconectat</span>
              )}
            </div>

            <div className="flex items-center justify-between py-1 border-b border-[#121620]">
              <span className="text-zinc-400 flex items-center gap-1.5">
                <span>🎯</span> Joc preferat
              </span>
              <span className="font-bold text-amber-400 uppercase">{user.favorite_game || user.favoriteGame || "CS2"}</span>
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-zinc-400 flex items-center gap-1.5">
                <span>🔫</span> Armă favorită
              </span>
              <span className="font-bold text-cyan-400">{user.loadout?.favorite_weapon || user.favorite_weapon || user.favoriteWeapon || "Desert Eagle"}</span>
            </div>
          </div>

          {/* 7. Action Buttons (Sponsor, Profil, Mesaj) */}
          <div className="pt-2 border-t border-[#141824] space-y-2">
            <Link 
              to="/donate?tab=vip" 
              className="w-full py-2.5 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/40 hover:border-amber-400 text-amber-300 font-chakra font-black uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-all shadow-md rounded-sm"
            >
              <Crown className="w-3.5 h-3.5 text-amber-400" />
              <span>SPONSOR / VIP</span>
            </Link>

            <div className="flex items-center gap-2">
              <Link 
                to={`/u/${user.username}`} 
                className="flex-1 py-2 bg-[#0c1018] hover:bg-[#121824] border border-[#181e2b] hover:border-[var(--tf-primary)]/60 text-white font-chakra font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm rounded-sm"
              >
                <UserIcon className="w-3.5 h-3.5 text-zinc-400" />
                <span>PROFIL</span>
              </Link>
              <Link 
                to={`/messages?new=${user.username}`} 
                className="flex-1 py-2 bg-[#0c1018] hover:bg-[#121824] border border-[#181e2b] hover:border-cyan-500/60 text-zinc-300 hover:text-cyan-300 font-chakra font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm rounded-sm"
              >
                <Mail className="w-3.5 h-3.5 text-zinc-400" />
                <span>MESAJ</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
